import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../lib/realtime';
import type { Vehicle, Trip, VehicleMaintenance } from '../../types';

async function log(bizId: string, type: string, desc: string, eType: string, eId: string, userId: string) {
  try { await supabase.from('activity_log').insert({ business_id: bizId, activity_type: type, description: desc, entity_type: eType, entity_id: eId, logged_by: userId }); } catch { /* */ }
}

// ── Vehicles ──

export function useVehicles(businessId: string | undefined) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      const { data, error: e } = await supabase.from('vehicles').select('*')
        .eq('business_id', businessId).order('name').limit(100).abortSignal(ctrl.signal);
      if (e) throw e;
      if (mountedRef.current) setVehicles((data ?? []) as Vehicle[]);
    } catch (e) { if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed'); }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId]);

  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  const rt = useMemo(() => ['vehicles'], []);
  useRealtimeSubscription(businessId, rt, fetch);

  const createVehicle = useCallback(async (input: Partial<Vehicle>, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { data, error: e } = await supabase.from('vehicles')
        .insert({ ...input, business_id: businessId, status: input.status || 'available' })
        .select('id').maybeSingle();
      if (e) return false;
      await log(businessId, 'vehicle_created', `Vehicle added: ${input.name}`, 'vehicle', data?.id ?? '', userId);
      fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  const updateVehicle = useCallback(async (id: string, updates: Partial<Vehicle>, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('vehicles').update(updates).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, 'vehicle_updated', `Vehicle updated: ${updates.name ?? id}`, 'vehicle', id, userId);
      fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  const retireVehicle = useCallback(async (id: string, name: string, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('vehicles').update({ status: 'retired' }).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, 'vehicle_retired', `Vehicle retired: ${name}`, 'vehicle', id, userId);
      fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  return { vehicles, loading, error, refresh: fetch, createVehicle, updateVehicle, retireVehicle };
}

// ── Trips ──

export interface TripRow extends Trip {
  vehicle_name?: string;
  user_name?: string;
}

export function useTrips(businessId: string | undefined) {
  const [trips, setTrips] = useState<TripRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [vehicleFilter, setVehicleFilter] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchPage = useCallback(async (p: number, reset: boolean) => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      let q = supabase.from('trips').select('*, vehicle:vehicles!vehicle_id(name), driver:users!user_id(first_name, last_name)')
        .eq('business_id', businessId).order('started_at', { ascending: false })
        .range(p * 25, (p + 1) * 25 - 1).abortSignal(ctrl.signal);
      if (vehicleFilter) q = q.eq('vehicle_id', vehicleFilter);
      const { data, error: e } = await q;
      if (e) throw e;
      const rows = (data ?? []).map((r: unknown) => {
        const row = r as Record<string, unknown>;
        const vehicle = row.vehicle as Record<string, string> | null;
        const driver = row.driver as Record<string, string> | null;
        return {
          ...(row as unknown as Trip),
          vehicle_name: vehicle?.name ?? null,
          user_name: driver ? `${driver.first_name} ${driver.last_name}` : null,
        };
      }) as TripRow[];
      if (mountedRef.current) {
        setTrips(prev => reset ? rows : [...prev, ...rows]);
        setHasMore(rows.length === 25);
        setPage(p);
      }
    } catch { /* */ }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId, vehicleFilter]);

  const loadFirst = useCallback(() => fetchPage(0, true), [fetchPage]);
  const loadMore = useCallback(() => { if (hasMore && !loading) fetchPage(page + 1, false); }, [fetchPage, hasMore, loading, page]);

  useEffect(() => { mountedRef.current = true; loadFirst(); return () => { mountedRef.current = false; }; }, [loadFirst]);

  // Summary
  const summary = useMemo(() => {
    const now = new Date();
    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const month = trips.filter(t => t.status === 'completed' && (t.started_at ?? '') >= monthStart);
    return {
      tripsThisMonth: month.length,
      kmThisMonth: Math.round(month.reduce((s, t) => s + (t.distance_km ?? 0), 0)),
      deductionsThisMonth: Math.round(month.reduce((s, t) => s + (t.mileage_deduction ?? 0), 0) * 100) / 100,
    };
  }, [trips]);

  return { trips, loading, hasMore, loadFirst, loadMore, summary, vehicleFilter, setVehicleFilter };
}

// ── Maintenance ──

export function useMaintenance(businessId: string | undefined) {
  const [records, setRecords] = useState<(VehicleMaintenance & { vehicle_name?: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [vehicleFilter, setVehicleFilter] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      let q = supabase.from('vehicle_maintenance').select('*, vehicle:vehicles!vehicle_id(name)')
        .order('scheduled_date', { ascending: false }).limit(100).abortSignal(ctrl.signal);
      if (vehicleFilter) q = q.eq('vehicle_id', vehicleFilter);
      const { data, error: e } = await q;
      if (e) throw e;
      const rows = (data ?? []).map((r: unknown) => {
        const row = r as Record<string, unknown>;
        const vehicle = row.vehicle as Record<string, string> | null;
        return { ...(row as unknown as VehicleMaintenance), vehicle_name: vehicle?.name ?? undefined };
      });
      if (mountedRef.current) setRecords(rows);
    } catch { /* */ }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId, vehicleFilter]);

  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const createMaintenance = useCallback(async (input: Partial<VehicleMaintenance>, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { data, error: e } = await supabase.from('vehicle_maintenance')
        .insert({ ...input }).select('id').maybeSingle();
      if (e) return false;
      await log(businessId, 'maintenance_scheduled', `Maintenance scheduled: ${input.description}`, 'vehicle_maintenance', data?.id ?? '', userId);
      fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  const completeMaintenance = useCallback(async (id: string, cost: number | null, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('vehicle_maintenance')
        .update({ status: 'completed', completed_date: new Date().toISOString().split('T')[0], cost });
      if (e) return false;
      await log(businessId, 'maintenance_completed', `Maintenance completed`, 'vehicle_maintenance', id, userId);
      fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  return { records, loading, refresh: fetch, vehicleFilter, setVehicleFilter, createMaintenance, completeMaintenance };
}
